import { Alert, Card, Col, Empty, Row, Skeleton, Statistic, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { Chart } from "react-google-charts";
import { useFetch } from "../../hooks/useFetch";
import { DiagnosticoOperacionalResumo } from "../../interfaces/DiagnosticoOperacionalResumo";
import { PeriodosProximosDoisAnosResumo } from "../../interfaces/PeriodosProximosDoisAnosResumo";
import { PostagemVendaPendentePagamento } from "../../interfaces/PostagemVendaPendentePagamento";
import { buscarDiagnosticoOperacionalResumoService } from "../../services/buscarDiagnosticoOperacionalResumoService";
import { buscarPeriodosProximosDoisAnosService } from "../../services/buscarPeriodosProximosDoisAnosService";
import { buscarPostagensVendaPendentesPagamentoService } from "../../services/buscarPostagensVendaPendentesPagamentoService";
import { Container, Description, Header, Title } from "./styles";

type PieChartRow = [string, number];

const STATUS_CHART_COLORS = ["#2f855a", "#d64545"];
const IRREGULARIDADE_CHART_COLORS = ["#0b5d6b", "#f0a202", "#c0392b"];
const PERIODOS_CHART_COLORS = ["#2f855a", "#d97706"];

function isSemAssinatura(assinatura?: string) {
  if (!assinatura) {
    return false;
  }

  return (
    assinatura
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase() === "nao"
  );
}

function buildStatusChartData(
  resumo: DiagnosticoOperacionalResumo
): (string | number)[][] {
  const rows: PieChartRow[] = [
    ["Sem problema", resumo.totalSemProblema],
    ["Com problema", resumo.totalComProblema],
  ];

  return [["Status", "Quantidade"], ...rows];
}

function buildIrregularidadeChartData(
  resumo: DiagnosticoOperacionalResumo
): (string | number)[][] {
  const rows: PieChartRow[] = [
    ["Empreendimento (EP)", resumo.totalProblemaEp],
    ["Apartamento (AP)", resumo.totalProblemaAp],
    ["Periodos (PEU)", resumo.totalProblemaPeu],
  ];

  return [["Irregularidade", "Quantidade"], ...rows];
}

function buildPeriodosChartData(
  resumo: PeriodosProximosDoisAnosResumo
): (string | number)[][] {
  const totalComPeriodos =
    resumo.totais?.cotasComPeriodos ??
    resumo.totais?.comPeriodos ??
    resumo.cotasComPeriodos?.length ??
    0;
  const totalSemPeriodos =
    resumo.totais?.cotasSemPeriodos ??
    resumo.totais?.semPeriodos ??
    resumo.cotasSemPeriodos?.length ??
    0;

  const rows: PieChartRow[] = [
    ["Com períodos", totalComPeriodos],
    ["Sem períodos", totalSemPeriodos],
  ];

  return [["Situação", "Quantidade de cotas"], ...rows];
}

function getRowsTotal(rows: (string | number)[][]) {
  return rows
    .slice(1)
    .reduce((total, currentRow) => total + Number(currentRow[1] || 0), 0);
}

interface PieChartCardProps {
  title: string;
  data: (string | number)[][];
  colors: string[];
  helperText?: string;
  onSliceSelect?: (sliceLabel: string) => void;
}

function PieChartCard({
  title,
  data,
  colors,
  helperText,
  onSliceSelect,
}: PieChartCardProps) {
  const hasData = getRowsTotal(data) > 0;

  return (
    <Card title={title} style={{ height: "100%" }}>
      {hasData ? (
        <>
          <Chart
            chartType="PieChart"
            width="100%"
            height="360px"
            data={data}
            loader={<Skeleton active paragraph={{ rows: 8 }} />}
            chartEvents={
              onSliceSelect
                ? [
                    {
                      eventName: "select",
                      callback: ({ chartWrapper }) => {
                        if (!chartWrapper) {
                          return;
                        }

                        const chart = chartWrapper.getChart();

                        if (!chart) {
                          return;
                        }

                        const selection = chart.getSelection();

                        if (!selection.length) {
                          return;
                        }

                        const selectedRow = selection[0]?.row;

                        if (typeof selectedRow !== "number") {
                          return;
                        }

                        const sliceLabel = String(data[selectedRow + 1]?.[0] || "");
                        onSliceSelect(sliceLabel);
                      },
                    },
                  ]
                : undefined
            }
            options={{
              pieHole: 0.42,
              legend: { position: "bottom" },
              chartArea: { width: "92%", height: "75%" },
              colors,
              backgroundColor: "transparent",
            }}
          />
          {helperText ? (
            <Typography.Text type="secondary">{helperText}</Typography.Text>
          ) : null}
        </>
      ) : (
        <Empty description="Nenhum dado disponivel para este grafico." />
      )}
    </Card>
  );
}

export default function ViewDashboardOperacional() {
  const navigate = useNavigate();
  const {
    data: resumo,
    isLoading,
    error,
  } = useFetch(true, buscarDiagnosticoOperacionalResumoService, []);
  const {
    data: postagensPendentesPagamento,
    isLoading: isLoadingPostagensPendentesPagamento,
    error: errorPostagensPendentesPagamento,
  } = useFetch(true, buscarPostagensVendaPendentesPagamentoService, []);
  const {
    data: resumoPeriodos,
    isLoading: isLoadingResumoPeriodos,
    error: errorResumoPeriodos,
  } = useFetch(true, buscarPeriodosProximosDoisAnosService, []);

  const postagensSemAssinatura =
    postagensPendentesPagamento?.filter((item: PostagemVendaPendentePagamento) =>
      isSemAssinatura(item.assinatura)
    ) ?? [];
  const totalPostagensSemAssinatura = postagensSemAssinatura.length;

  const summaryCards = resumo
    ? [
        {
          title: "Total de CTU",
          value: resumo.totalCtu,
          valueStyle: { color: "#003644" },
        },
        {
          title: "CTU sem problema",
          value: resumo.totalSemProblema,
          valueStyle: { color: "#2f855a" },
        },
        {
          title: "CTU com problema",
          value: resumo.totalComProblema,
          valueStyle: { color: "#d64545" },
        },
        {
          title: "PEU com problema",
          value: resumo.totalProblemaPeu,
          valueStyle: { color: "#c0392b" },
        },
        {
          title: "Postagens de venda sem assinatura",
          value: totalPostagensSemAssinatura,
          valueStyle: {
            color:
              totalPostagensSemAssinatura > 0 ? "#d97706" : "#003644",
          },
          clickable: totalPostagensSemAssinatura > 0,
          onClick: () =>
            navigate("/dashboardOperacional/postagens-venda-sem-assinatura"),
        },
      ]
    : [];

  return (
    <Container>
      <Header>
        <Title>Dashboard Operacional</Title>
        <Description>
          Acompanhe rapidamente as CTU que estão regulares e as que exigem
          atenção operacional imediata. O indicador de períodos dos próximos
          dois anos considera somente empreendimentos de cronograma fixo.
        </Description>
      </Header>

      {isLoading || isLoadingPostagensPendentesPagamento ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="Nao foi possivel carregar o resumo operacional."
          description="Verifique a conexao com a API e tente novamente."
        />
      ) : !resumo ? (
        <Alert
          type="warning"
          showIcon
          message="Nenhum resumo operacional foi retornado."
        />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {summaryCards.map((item) => (
              <Col xs={24} sm={12} lg={6} key={item.title}>
                <Card
                  hoverable={Boolean(item.clickable)}
                  onClick={item.clickable ? item.onClick : undefined}
                  style={{
                    height: "100%",
                    cursor: item.clickable ? "pointer" : "default",
                  }}
                >
                  <Statistic
                    title={item.title}
                    value={item.value}
                    valueStyle={item.valueStyle}
                  />
                  {item.clickable ? (
                    <Typography.Text type="secondary">
                      Clique para abrir a listagem.
                    </Typography.Text>
                  ) : null}
                </Card>
              </Col>
            ))}
          </Row>

          {errorPostagensPendentesPagamento ? (
            <Alert
              type="warning"
              showIcon
              message="Nao foi possivel carregar o contador de postagens de venda sem assinatura."
              description="Os demais indicadores do dashboard continuam disponiveis."
            />
          ) : null}

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <PieChartCard
                title="Cotas Usuarios (CTU): com problemas e sem problemas"
                data={buildStatusChartData(resumo)}
                colors={STATUS_CHART_COLORS}
                helperText="Clique na fatia Com problema para abrir a lista das CTUs com irregularidades."
                onSliceSelect={(sliceLabel) => {
                  if (sliceLabel === "Com problema") {
                    navigate("/dashboardOperacional/ctus-com-problema");
                  }
                }}
              />
            </Col>
            <Col xs={24} lg={12}>
              <PieChartCard
                title="CTU com problemas por tipo de irregularidade"
                data={buildIrregularidadeChartData(resumo)}
                colors={IRREGULARIDADE_CHART_COLORS}
              />
            </Col>
          </Row>

          {isLoadingResumoPeriodos ? (
            <Card title="Cotas de cronograma fixo com períodos nos próximos dois anos">
              <Skeleton active paragraph={{ rows: 8 }} />
            </Card>
          ) : errorResumoPeriodos ? (
            <Alert
              type="warning"
              showIcon
              message="Não foi possível carregar as cotas com períodos nos próximos dois anos."
              description="Os demais indicadores do dashboard continuam disponíveis."
            />
          ) : resumoPeriodos ? (
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <PieChartCard
                  title="Cotas de cronograma fixo com períodos nos próximos dois anos"
                  data={buildPeriodosChartData(resumoPeriodos)}
                  colors={PERIODOS_CHART_COLORS}
                  helperText={`Clique em Sem períodos para abrir a listagem. Universo elegível: ${
                    resumoPeriodos.totais?.empreendimentos ?? 0
                  } empreendimento(s), ${
                    resumoPeriodos.totais?.apartamentos ?? 0
                  } apartamento(s) e ${
                    resumoPeriodos.totais?.cotas ?? 0
                  } cota(s).`}
                  onSliceSelect={(sliceLabel) => {
                    if (sliceLabel === "Sem períodos") {
                      navigate(
                        "/dashboardOperacional/cotas-cronograma-fixo-sem-periodos"
                      );
                    }
                  }}
                />
              </Col>
            </Row>
          ) : (
            <Alert
              type="warning"
              showIcon
              message="Nenhum resumo de períodos foi retornado."
            />
          )}
        </>
      )}
    </Container>
  );
}
